import type { CurriculumItemReason } from "@/modules/lernpfad/engine"

/**
 * Chart palette for the Lernpfad demo.
 *
 * VALIDATED, not eyeballed — both modes pass all six checks of
 * `dataviz/scripts/validate_palette.js` (lightness band, chroma floor, CVD
 * adjacent separation, normal-vision floor, contrast vs surface):
 *
 *   light  #b3261e,#7c5fb0,#b45309,#1d4ed8  → ALL CHECKS PASS
 *   dark   #dd6257,#9d80d0,#c48232,#6892e6  → ALL CHECKS PASS
 *
 * The dark steps are SELECTED, not an automatic lightening of the light ones —
 * a naive flip lands outside the dark lightness band (0.48–0.67), which is
 * exactly what the first two candidates failed on.
 *
 * Hue order is fixed and tied to the reason, never to rank or to how many
 * categories happen to be present: a plan with no `ziel` items must not repaint
 * the survivors.
 */

export const REASON_ORDER: CurriculumItemReason[] = [
    "pflicht",
    "auffrischung",
    "voraussetzung",
    "luecke",
    "ziel",
]

/** CSS custom properties, defined once on the demo root (see `chart-theme.tsx`). */
export const REASON_VAR: Record<CurriculumItemReason, string> = {
    pflicht: "var(--lp-pflicht)",
    // A spaced booster is the SAME duty, scheduled again — not a fifth category.
    // Sharing the hue says that; a fifth hue would deny it, and would also have
    // to be re-validated against the CVD checks above, compressing the adjacent
    // separation the current four were selected for. The pattern below carries
    // the distinction instead, which is the correct job for a modifier.
    auffrischung: "var(--lp-pflicht)",
    voraussetzung: "var(--lp-voraussetzung)",
    luecke: "var(--lp-luecke)",
    ziel: "var(--lp-ziel)",
}

/**
 * Reasons rendered with a diagonal hatch over their hue.
 *
 * Redundant encoding on purpose: the hatch is what separates a booster from the
 * duty it reinforces for a viewer who cannot rely on the fill, and it survives
 * greyscale printing — which an L&D deck does end up doing.
 */
export const REASON_HATCHED: Record<CurriculumItemReason, boolean> = {
    pflicht: false,
    auffrischung: true,
    voraussetzung: false,
    luecke: false,
    ziel: false,
}

/** Background shorthand for a mark, hatch included where the reason calls for it. */
export function reasonFill(reason: CurriculumItemReason): string {
    const colour = REASON_VAR[reason]
    if (!REASON_HATCHED[reason]) return colour
    return `repeating-linear-gradient(45deg, ${colour} 0 4px, color-mix(in srgb, ${colour} 55%, transparent) 4px 8px)`
}

/**
 * Status colors — RESERVED. Never reused as a categorical slot, and always
 * shipped alongside an icon and a label, never as colour alone.
 */
export const STATUS_VAR = {
    critical: "var(--lp-critical)",
    warning: "var(--lp-warning)",
    good: "var(--lp-good)",
    /** Recessive fill for "the rest of the budget". */
    track: "var(--lp-track)",
    grid: "var(--lp-grid)",
} as const

/** Single-hue sequential ramp for magnitude (coverage, demand). */
export const SEQUENTIAL_VAR = "var(--lp-seq)"

/** 2px surface gap between adjacent fills, per the mark spec. */
export const MARK_GAP = 2
/** Rounded data-end radius. */
export const MARK_RADIUS = 4
